/**
 * commerce — 무료 개방 모드(029) 설정·발급 API.
 *
 * - GET  /fo/commerce-config            : 무료 개방 여부·누적 한도(비인증 공개)
 * - GET  /fo/orders/free-claim/quota    : 계정별 잔여 수령 가능 수량(인증)
 * - POST /fo/orders/free-claim          : 무료 발급(인증, PG 미경유)
 * - GET  /fo/free-events/{group}/me     : 상품군 무료 이벤트 내 수령 상태(인증, 081)
 * - POST /fo/free-events/{group}/claim  : 상품군 무료판 받기(인증, 081)
 *
 * baseURL = /api → 실제 경로는 /api/fo/** (백엔드 FO prefix 규약).
 * 에러는 http.ts 인터셉터가 1회 토스트로 처리하고 그대로 reject 한다 — 호출부에서 별도 알림을 띄우지 않는다.
 */
import { http } from '#/api/http';

/** 커머스 공개 설정 — 무료 개방 여부와 누적 수령 한도(개인 정보 미포함). */
export interface CommerceConfig {
  /** 무료 개방 활성 여부. */
  freeMode: boolean;
  /** 계정당 누적 수령 한도(0 = 무제한). */
  claimLimit: number;
  /**
   * 한시 무료 이벤트(050) 활성 여부 — 배너 노출·`/event` 종료 상태·Packages 참가자 분기의 근거.
   * 이벤트 기간에도 freeMode 는 false 를 유지한다(전역 무료 모드 미사용 — 결제 동선 무손상).
   */
  reviewEvent: boolean;
  /**
   * 상품군 무료 이벤트(081) 공개 상태 — 설정된 이벤트만, 닫힌 이벤트도 open=false 로 포함.
   * 050 reviewEvent 와 독립이다(한쪽 개방이 다른 쪽 배너·안내를 열지 않는다).
   */
  freeEvents: FreeEventSummary[];
}

/** 상품군 무료 이벤트 공개 상태 1건(081). */
export interface FreeEventSummary {
  /** 상품군 코드(예: INTERVIEW_ROADMAP). */
  groupCode: string;
  /** 지금 받을 수 있는지(기간 ∧ 킬스위치 ∧ 대상 준비 — 서버 판정). */
  open: boolean;
  /** 종료 경계(ISO-8601 UTC, 없으면 null) — 표시 시 formatKst 경유. */
  endsAt: string | null;
}

/** 내 상품군 무료 이벤트 상태(081). */
export interface FreeEventStatus {
  groupCode: string;
  /** 지금 받을 수 있는지. */
  open: boolean;
  /** 무료판 보유 여부(종료 후에도 판정). */
  claimed: boolean;
  /** 보유 시 무료 주문 id. */
  orderId: number | null;
}

/** 상품군 무료판 받기 결과(081). */
export interface FreeEventClaimResult {
  orderId: number;
  orderNo: string;
  /** 지급 시각(ISO-8601, 멱등 재시도면 null). */
  claimedAt: string | null;
}

/** 계정별 잔여 수령 가능 수량 — 판정 SSoT 는 서버이며 프론트는 표시만 한다. */
export interface FreeClaimQuota {
  /** 적용 한도(0 이면 무제한). */
  limit: number;
  /** 현재 활성 보유 권한 수. */
  owned: number;
  /** 잔여 수령 가능 수량(무제한이면 의미 없음). */
  remaining: number;
  /** 무제한 여부 — limit==0 을 프론트가 재해석하지 않도록 서버가 내려준다. */
  unlimited: boolean;
}

/** 무료 발급 결과. */
export interface FreeClaimResult {
  /** 생성된 주문 식별자. */
  orderId: number;
  /** 표시용 주문번호. */
  orderNo: string;
  /** 신규 부여된 권한 수. */
  grantedCount: number;
  /** 발급 시각(ISO-8601 문자열 — 표시 시 formatKst 경유). */
  claimedAt: string;
}

/**
 * 커머스 공개 설정 조회 — 비인증.
 *
 * 무료 모드가 꺼져 있어도 엔드포인트는 존재하며 `freeMode:false` 를 반환한다(프론트 분기 없는 단일 호출).
 * @param options.silent 실패 토스트 억제 — 백그라운드 재검증(스토어 revalidate)처럼 실패가 기대 흐름인
 *   호출용. 부트 로드(loadConfig)는 기본값(토스트 1회)을 유지한다.
 * @returns 무료 개방 여부·누적 한도·이벤트 여부
 */
export async function fetchCommerceConfig(options?: { silent?: boolean }): Promise<CommerceConfig> {
  const { data } = await http.get<CommerceConfig>('/fo/commerce-config', {
    silent: options?.silent === true,
  } as Parameters<typeof http.get>[1]);
  return data;
}

/**
 * 잔여 수령 가능 수량 조회 — 인증 필요(비로그인 호출 금지).
 * @returns 한도·보유·잔여·무제한 여부
 */
export async function fetchFreeClaimQuota(): Promise<FreeClaimQuota> {
  const { data } = await http.get<FreeClaimQuota>('/fo/orders/free-claim/quota');
  return data;
}

/**
 * 무료 발급 — 장바구니 콘텐츠 전체에 다운로드 권한을 부여한다(PG 미경유).
 *
 * `idempotencyKey` 는 **한 번의 발급 시도에 하나**를 만들어 재전송(더블클릭·네트워크 재시도)에 같은 값을 재사용한다.
 * 호출마다 새 키를 만들면 서버 멱등이 무력화돼 중복 주문이 생긴다.
 *
 * @param contentIds 발급 대상 content_item.id 목록(cart contentIds getter)
 * @param idempotencyKey 발급 시도 단위 중복 차단 키(UUID v4)
 * @returns 생성된 주문·부여 권한 수
 */
export async function postFreeClaim(
  contentIds: number[],
  idempotencyKey: string,
): Promise<FreeClaimResult> {
  const { data } = await http.post<FreeClaimResult>('/fo/orders/free-claim', {
    contentIds,
    idempotencyKey,
  });
  return data;
}

/**
 * 상품군 무료 이벤트 내 수령 상태 — 인증 필요(081 contracts §2).
 * @param groupCode 상품군 코드
 */
export async function fetchFreeEventStatus(groupCode: string): Promise<FreeEventStatus> {
  const { data } = await http.get<FreeEventStatus>(
    `/fo/free-events/${encodeURIComponent(groupCode)}/me`,
  );
  return data;
}

/**
 * 상품군 무료판 받기 — 인증 필요, 대상은 서버가 결정한다(081 contracts §3).
 *
 * `idempotencyKey` 는 받기 시도 1회에 하나 — 재전송(더블클릭·재시도)에 같은 값을 재사용한다.
 * 409(이미 받음)·400(종료) 안내는 http.ts 인터셉터 토스트가 담당한다.
 *
 * @param groupCode 상품군 코드
 * @param idempotencyKey 시도 단위 중복 차단 키(UUID v4)
 */
export async function claimFreeEvent(
  groupCode: string,
  idempotencyKey: string,
): Promise<FreeEventClaimResult> {
  const { data } = await http.post<FreeEventClaimResult>(
    `/fo/free-events/${encodeURIComponent(groupCode)}/claim`,
    { idempotencyKey },
  );
  return data;
}
